/* Padel USA comparator. Complete, attributed, verified-source profiles only.
 * Contract and rubric: custom-code-padelusa/features/racket-comparator.md. */
(() => {
  'use strict';

  const AXES = ['power', 'comfort', 'spin', 'control', 'forgiveness', 'handling'];
  const LABELS = ['Power', 'Comfort', 'Spin', 'Control', 'Forgiveness', 'Handling'];
  const MAX = 3;
  const STORAGE = 'pusa-racket-comparison-v1';
  const list = value => Array.isArray(value) ? value : value ? [value] : [];
  const norm = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const display = value => list(value).filter(Boolean).slice().sort().join(' / ') || 'Not listed';
  // OS analytics (2026-10-12): pusa_comparator_* events go to Shopify.analytics.publish, which the
  // "PadelUSA OS analytics" custom pixel forwards to os.padelusa.com/analytics (guided-shopping
  // share). Payloads carry catalog handles, product ids and counts only. Never throws.
  const publish = (name, data) => {
    try { window.Shopify?.analytics?.publish?.(name, data); } catch { /* Analytics must never break the comparator. */ }
  };
  const handleOf = href => { try { return (new URL(href, location.origin).pathname.match(/\/products\/([^/?#]+)/) || [])[1] || null; } catch { return null; } };
  // No performance score is invented from shape, materials or merchant tags.
  function estimate() { return Object.fromEntries(AXES.map(key => [key, null])); }

  function profile(p, estimates = true, externalRatings = {}) {
    let rating = p.scores;
    try { if (typeof rating === 'string') rating = JSON.parse(rating); } catch { rating = null; }
    const valid = rating && rating.source === 'play_test' && rating.verified === true &&
      typeof rating.evidence_url === 'string' && rating.evidence_url.startsWith('https://') &&
      typeof rating.reviewed_by === 'string' && rating.reviewed_by.trim() &&
      /^\d{4}-\d{2}-\d{2}$/.test(rating.reviewed_at || '') &&
      AXES.every(key => typeof rating[key] === 'number' && Number.isFinite(rating[key]) && rating[key] >= 0 && rating[key] <= 10);
    if (valid) return { values: Object.fromEntries(AXES.map(key => [key, rating[key]])),
      source: 'play_test', label: `Play-tested · ${rating.reviewed_by} · ${rating.reviewed_at}` };
    // PDP "How it plays" (2026-10-08): values the theme verified server-side (identity + generated
    // projection) and sent without provenance, one racket at a time. Never set from the catalog JSON.
    const verified = p.verifiedValues;
    if (verified && AXES.every(key => typeof verified[key] === 'number' && Number.isFinite(verified[key]) && verified[key] >= 0 && verified[key] <= 10))
      return { values: Object.fromEntries(AXES.map(key => [key, verified[key]])), source: 'external_rating', label: 'Independent reviewer rating' };
    const external = externalRatings[p.handle];
    if (external?.source === 'external_rating' && external.verification_status === 'ready' &&
      external.catalog_product_id === p.id && external.catalog_title === p.title && external.catalog_vendor === p.vendor &&
      (external.catalog_year ?? null) === (p.year ?? null) &&
      Array.isArray(external.matched_gtins) &&
      JSON.stringify([...new Set(list(p.gtins).map(v => String(v || '').trim().padStart(14,'0')))].sort()) === JSON.stringify([...external.matched_gtins].sort()) && external.scale_max === 100 &&
      typeof external.provider === 'string' && external.provider.trim() &&
      typeof external.reviewed_by === 'string' && external.reviewed_by.trim() &&
      /^\d{4}-\d{2}-\d{2}$/.test(external.observed_on || '') &&
      typeof external.source_url === 'string' && external.source_url.startsWith('https://') &&
      AXES.every(key => typeof external[key] === 'number' && Number.isFinite(external[key]) && external[key] >= 0 && external[key] <= 100)) {
      return { values: Object.fromEntries(AXES.map(key => [key, external[key] / 10])),
        source: 'external_rating', label: `${external.provider} · ${external.reviewed_by}`,
        sourceUrl: external.source_url, observedOn: external.observed_on };
    }
    return { values: estimates ? estimate(p) : Object.fromEntries(AXES.map(key => [key, null])),
      source: 'estimate', label: 'Profile awaiting verification' };
  }

  function verifiedProduct(product, reply) {
    if (!reply || reply.id !== product.id || reply.handle !== product.handle || reply.title !== product.title || reply.vendor !== product.vendor ||
      !reply.verifiedValues || !AXES.every(key => typeof reply.verifiedValues[key] === 'number' && Number.isFinite(reply.verifiedValues[key]) && reply.verifiedValues[key] >= 0 && reply.verifiedValues[key] <= 10))
      throw new Error('Product profile identity or values changed');
    const allowed = ['shape','bal','core','hand','wt','weightDisplay','surf','lvl','play','face','frame','rubber'];
    const specs = Object.fromEntries(Object.entries(reply.verifiedSpecs || {}).filter(([key]) => allowed.includes(key)));
    return { ...product, ...specs, verifiedValues: Object.fromEntries(AXES.map(key => [key, reply.verifiedValues[key]])) };
  }

  function selectHandles(handles, products) {
    const allowed = new Set(products.map(p => p.handle));
    return [...new Set(Array.isArray(handles) ? handles : [])].filter(h => typeof h === 'string' && allowed.has(h)).slice(0, MAX);
  }

  function comparisonCapacity(handles, thirdRequested = false) {
    return thirdRequested || handles.length >= MAX ? MAX : 2;
  }

  function scoreDetails(products, axis, activeIndex, externalRatings = {}) {
    if (!Number.isInteger(axis) || axis < 0 || axis >= AXES.length) return '';
    return `<div class="rc__tooltip-head"><strong>${LABELS[axis]}</strong><span>Score</span></div><ul>${products.map((p, i) => {
      const value = profile(p, false, externalRatings).values[AXES[axis]];
      return `<li class="rc__series--${i}${i === activeIndex ? ' rc__tooltip-active' : ''}"><span class="rc__color-key" aria-hidden="true"></span><span>${i + 1}. ${esc(p.title)}</span><strong>${value === null ? 'Not enough data' : value}</strong></li>`;
    }).join('')}</ul>`;
  }

  // Remove binary subtraction noise from derived gaps; the original ratings never change.
  const ratingGap = (a, b) => Number((a - b).toPrecision(12));

  function ratingComparisons(products, externalRatings = {}) {
    const profiles = products.map(p => profile(p, false, externalRatings));
    return AXES.map((key, axis) => {
      const values = profiles.map(p => p.values[key]);
      const comparable = values.length > 1 && values.every(value => typeof value === 'number' && Number.isFinite(value));
      const high = comparable ? Math.max(...values) : null;
      const low = comparable ? Math.min(...values) : null;
      return { key, axis, label: LABELS[axis], values, comparable, high, low,
        spread: comparable ? ratingGap(high, low) : null,
        highest: comparable ? values.flatMap((value, i) => value === high ? [i] : []) : [] };
    });
  }

  function tradeoffSummary(products, comparisons) {
    if (products.length < 2) return '';
    const comparable = comparisons.filter(row => row.comparable);
    if (!comparable.length) return '<p class="rc__summary-empty">More verified ratings are needed to summarize the trade-offs.</p>';
    if (comparable.every(row => row.spread === 0)) return `<p class="rc__summary-empty">${comparable.length === AXES.length ? 'These rackets have the same ratings across all six attributes.' : 'The available ratings are the same.'} Any specification differences are listed below.</p>`;
    return `<h3 class="rc__summary-heading">Main trade-offs</h3><div class="rc__tradeoff-grid" style="--rc-columns:${products.length}">${products.map((p, i) => {
      const ranked = comparable.map(row => ({ label: row.label, axis: row.axis,
        gap: ratingGap(row.values[i], Math.max(...row.values.filter((_, index) => index !== i))) }))
        .filter(row => row.gap !== 0).sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap) || a.axis - b.axis);
      const higher = ranked.find(row => row.gap > 0), lower = ranked.find(row => row.gap < 0);
      const gaps = (higher && lower ? [higher, lower] : ranked.slice(0, 2)).sort((a, b) => a.axis - b.axis);
      return `<article class="rc__tradeoff rc__series--${i}"><h4><span class="rc__color-key" aria-hidden="true"></span>${i + 1}. ${esc(p.title)}</h4>${gaps.length ? `<ul>${gaps.map(row => `<li><span>${row.label}</span><span><strong>${row.gap > 0 ? '+' : '−'}${Math.abs(row.gap)}</strong> ${row.gap > 0 ? 'higher' : 'lower'}</span></li>`).join('')}</ul>` : '<p>Matches the highest selected rating on each available attribute.</p>'}</article>`;
    }).join('')}</div><p class="rc__meta rc__tradeoff-note">Key rating trade-offs, out of 10. Gaps compare each racket with the highest rating among the other selections.</p>`;
  }

  function ratingCells(row, differencesOnly, products) {
    return row.values.map((value, i) => {
      const emphasis = differencesOnly && row.comparable && row.spread > 0;
      const highest = emphasis && value === row.high;
      const label = highest ? (row.highest.length > 1 ? 'Tied highest' : products.length > 2 ? 'Highest' : 'Higher') : '';
      const gap = emphasis && !highest ? ratingGap(row.high, value) : null;
      return `<td class="rc__series--${i}${highest ? ' rc__score-high' : ''}">${value === null ? '<span class="rc__meta">Not enough data</span>' : `<span class="rc__score-value">${value}</span>`}${label ? `<span class="rc__score-label">${label}</span>` : gap !== null ? `<span class="rc__score-gap" aria-label="${gap} below the highest selected rating">−${gap}</span>` : ''}</td>`;
    }).join('');
  }

  function ratingsForTheme(data, role, enabled = true, publishedRelease = false) {
    const permitted = role === 'development' || role === 'unpublished' || (role === 'main' && publishedRelease);
    return enabled && permitted ? data.externalRatings || {} : {};
  }

  function search(products, query, brand, stockOnly, selected) {
    const terms = norm(query).trim().split(/\s+/).filter(Boolean);
    return products.filter(p => (!stockOnly || p.available) && (!brand || p.vendor === brand) &&
      !selected.includes(p.handle) && terms.every(term => norm(`${p.title} ${p.vendor}`).includes(term)));
  }

  function svgChart(products, estimates, externalRatings = {}) {
    const center = 230, radius = 151;
    const point = (index, score) => {
      const a = index * Math.PI / 3 - Math.PI / 2;
      return [center + Math.cos(a) * radius * score / 10, center + Math.sin(a) * radius * score / 10];
    };
    const points = score => AXES.map((_, i) => point(i, score).map(n => n.toFixed(2)).join(',')).join(' ');
    let markup = '<svg viewBox="0 0 460 460" role="group" aria-label="Racket profiles across six attributes. Focus or tap a point for exact scores; arrow keys move between attributes.">';
    for (const score of [2, 4, 6, 8, 10]) {
      markup += `<polygon class="rc__grid-line" points="${points(score)}"/><text class="rc__tick" x="${center + 12}" y="${center - radius * score / 10 + 14}">${score}</text>`;
    }
    AXES.forEach((_, i) => {
      const [x, y] = point(i, 10), [lx, ly] = point(i, 12.5);
      markup += `<line class="rc__grid-line" x1="${center}" y1="${center}" x2="${x}" y2="${y}"/><text class="rc__axis" x="${lx}" y="${ly}" text-anchor="middle" dominant-baseline="middle">${LABELS[i]}</text>`;
    });
    let pointMarkup = '';
    products.forEach((p, index) => {
      const values = profile(p, estimates, externalRatings).values;
      const known = AXES.map(key => values[key]);
      // A profile is a closed six-axis polygon. Partial data stays in the table, not a broken plot.
      if (known.some(value => value === null)) return;
      markup += `<g class="rc__series rc__series--${index}" data-rc-series="${index}">`;
      markup += `<polygon class="rc__polygon" points="${known.map((score, i) => point(i, score).join(',')).join(' ')}" data-rc-polygon="${index}"/>`;
      markup += '</g>';
      // All points sit above all polygon fills, including points on the first racket.
      pointMarkup += `<g class="rc__series rc__series--${index}" data-rc-series="${index}">`;
      known.forEach((value, i) => {
        if (value === null) return;
        const [x, y] = point(i, value);
        pointMarkup += `<g class="rc__point" role="button" tabindex="${i === 0 ? '0' : '-1'}" data-rc-point="${i}" data-rc-racket="${index}" aria-label="${esc(p.title)} — ${LABELS[i]}: ${value} out of 10"><circle class="rc__point-hit" cx="${x}" cy="${y}" r="32"/><circle class="rc__point-dot" cx="${x}" cy="${y}" r="4"/></g>`;
      });
      pointMarkup += '</g>';
    });
    return markup + pointMarkup + '</svg>';
  }

  function performanceSummary(p, externalRatings = {}) {
    const values = profile(p, false, externalRatings).values;
    return `<h3 class="rc__summary-heading">Performance at a glance</h3><div class="rc__performance-grid rc__series--0">${AXES.map((key, axis) => {
      const value = values[key];
      return `<div class="rc__performance-score"><div class="rc__performance-value"><span>${LABELS[axis]}</span><strong>${value === null ? 'Not available' : value}</strong></div>${value === null ? '' : `<div class="rc__performance-meter" aria-hidden="true" style="--rc-score:${value * 10}%"><span></span></div>`}</div>`;
    }).join('')}</div><p class="rc__meta rc__tradeoff-note">Add a second racket to see its trade-offs against this one.</p>`;
  }

  function purchaseMarkup(p, placement = 'card') {
    // Markup comes only from this section's native Liquid product-form renderer.
    // The graph repeats the action with a distinct form ID, not a second cart writer.
    return typeof p.purchaseHtml === 'string' ? p.purchaseHtml.replaceAll('RCForm-', `RCForm-${placement}-`) :
      `<a class="button rc__buy" href="${esc(p.url)}">${p.available ? 'Choose options' : 'Check availability'}</a>`;
  }

  // Pure functions are also used by the small contract test; the storefront needs no libraries.
  if (typeof module !== 'undefined' && module.exports) module.exports = { AXES, estimate, profile, verifiedProduct, search, selectHandles, svgChart, ratingsForTheme, comparisonCapacity, scoreDetails, ratingComparisons, tradeoffSummary, ratingCells, performanceSummary, purchaseMarkup };
  // Exact-score panel for the radar: hover a polygon/point, focus a point (arrow keys move between
  // attributes), tap or Enter to pin, Close/Escape/outside to dismiss. Shared by the comparator page and
  // the PDP "How it plays" module (assets/racket-play-profile.js) so both behave identically.
  class ScoreTooltip {
    constructor({ chart, tooltip, products, ratings, mobile }) {
      Object.assign(this, { chart, tooltip, products, ratings, mobile });
      this.pinned = false;
      this.shownKey = null;
    }

    // Full event wiring for consumers without their own handlers (the PDP module).
    bind(signal) {
      const on = (element, event, handler) => element.addEventListener(event, handler, { signal });
      on(this.chart, 'pointermove', event => this.hover(event));
      on(this.chart, 'pointerleave', () => { if (!this.pinned && !this.chart.contains(document.activeElement)) this.hide(); });
      on(this.chart, 'focusin', event => {
        const point = event.target.closest('[data-rc-point]');
        if (point) this.show(Number(point.dataset.rcPoint), Number(point.dataset.rcRacket));
      });
      on(this.chart, 'focusout', event => { if (!this.chart.contains(event.relatedTarget)) this.hide(); });
      on(this.chart, 'keydown', event => this.key(event));
      on(this.chart, 'click', event => {
        const point = this.pointAt(event);
        const polygon = event.target.closest('[data-rc-polygon]');
        if (point) {
          if (event.detail > 0) point.focus({ preventScroll: true });
          this.show(Number(point.dataset.rcPoint), Number(point.dataset.rcRacket), this.mobile.matches);
        } else if (polygon) this.show(this.axisAt(event), Number(polygon.dataset.rcPolygon), this.mobile.matches);
        else if (event.target.closest('[data-rc-score-close]')) this.hide();
      });
      on(document, 'pointerdown', event => { if (!this.chart.contains(event.target)) this.hide(); });
      on(document, 'keydown', event => { if (event.key === 'Escape') this.hide(); });
      on(window, 'resize', () => this.hide());
    }

    hide() {
      if (this.tooltip.contains(document.activeElement)) this.chart.querySelector('.rc__point-active')?.focus({ preventScroll: true });
      this.pinned = false;
      this.shownKey = null;
      this.tooltip.hidden = true;
      this.chart.querySelectorAll('.rc__series').forEach(series => series.classList.remove('rc__series-active', 'rc__series-muted'));
      this.chart.querySelectorAll('.rc__point-active').forEach(point => point.classList.remove('rc__point-active'));
    }

    hover(event) {
      if (this.tooltip.contains(event.target)) return;
      if (event.pointerType === 'touch' || this.pinned) return;
      const point = this.pointAt(event);
      const polygon = event.target.closest('[data-rc-polygon]');
      if (point) this.show(Number(point.dataset.rcPoint), Number(point.dataset.rcRacket));
      else if (polygon) {
        this.show(this.axisAt(event), Number(polygon.dataset.rcPolygon));
      } else if (!this.chart.contains(document.activeElement)) this.hide();
    }

    pointAt(event) {
      const target = event.target.closest('[data-rc-point]');
      if (!target || (event.type === 'click' && event.detail === 0)) return target;
      // Expanded hit areas can overlap. Pick the nearest actual dot, not the last SVG layer.
      let nearest = target, distance = Infinity;
      this.chart.querySelectorAll('[data-rc-point]').forEach(point => {
        const rect = point.querySelector('.rc__point-dot').getBoundingClientRect();
        const squared = (event.clientX - rect.left - rect.width / 2) ** 2 + (event.clientY - rect.top - rect.height / 2) ** 2;
        if (squared < distance) { distance = squared; nearest = point; }
      });
      return nearest;
    }

    axisAt(event) {
      const rect = this.chart.querySelector('svg').getBoundingClientRect();
      const angle = Math.atan2(event.clientY - rect.top - rect.height / 2, event.clientX - rect.left - rect.width / 2);
      return (Math.round((angle + Math.PI / 2) / (Math.PI / 3)) + AXES.length) % AXES.length;
    }

    show(axis, index, pinned = false) {
      const products = this.products();
      const point = this.chart.querySelector(`[data-rc-racket="${index}"][data-rc-point="${axis}"]`);
      if (!point) return;
      this.pinned = pinned || this.pinned;
      const key = `${axis}:${index}`;
      if (key !== this.shownKey) {
        this.tooltip.querySelector('[data-rc-score-details]').innerHTML = scoreDetails(products, axis, index, this.ratings());
        this.tooltip.setAttribute('aria-label', `${LABELS[axis]} scores`);
        this.chart.querySelectorAll('.rc__series').forEach(series => {
          series.classList.toggle('rc__series-active', Number(series.dataset.rcSeries) === index);
          series.classList.toggle('rc__series-muted', Number(series.dataset.rcSeries) !== index);
        });
        this.chart.querySelectorAll('[data-rc-point]').forEach(p => p.classList.toggle('rc__point-active', p === point));
        this.shownKey = key;
      }
      this.tooltip.hidden = false;
      if (!this.mobile.matches) {
        const chartRect = this.chart.getBoundingClientRect();
        const dotRect = point.querySelector('.rc__point-dot').getBoundingClientRect();
        const left = dotRect.left - chartRect.left + dotRect.width / 2 - this.tooltip.offsetWidth / 2;
        const above = dotRect.top - chartRect.top - this.tooltip.offsetHeight - 12;
        this.tooltip.style.left = `${Math.max(0, Math.min(chartRect.width - this.tooltip.offsetWidth, left))}px`;
        this.tooltip.style.top = `${above >= 0 ? above : dotRect.bottom - chartRect.top + 12}px`;
      } else { this.tooltip.style.removeProperty('left'); this.tooltip.style.removeProperty('top'); }
    }

    key(event) {
      const point = event.target.closest('[data-rc-point]');
      if (!point) return;
      const axis = Number(point.dataset.rcPoint), index = Number(point.dataset.rcRacket);
      if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? 5 : (axis + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : 5)) % 6;
        const target = this.chart.querySelector(`[data-rc-racket="${index}"][data-rc-point="${next}"]`);
        this.chart.querySelectorAll(`[data-rc-racket="${index}"]`).forEach(p => p.setAttribute('tabindex', p === target ? '0' : '-1'));
        target.focus({ preventScroll: true });
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.show(axis, index, true);
      }
    }
  }

  // Browser consumers (the PDP "How it plays" module, assets/racket-play-profile.js) share the same
  // validation, chart and gap logic instead of forking it. Read-only; never mutates catalog data.
  if (typeof window !== 'undefined') window.PadelRacketCompare = Object.freeze({ AXES, LABELS, profile, search, svgChart, ratingsForTheme, ratingComparisons, ScoreTooltip });
  if (typeof window === 'undefined' || !window.customElements || customElements.get('racket-comparator')) return;

  class RacketComparator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const on = (element, event, handler) => element.addEventListener(event, handler, { signal: this.abort.signal });
      this.status = this.querySelector('[data-rc-status]');
      try {
        const data = JSON.parse(this.querySelector('[data-rc-products]').textContent);
        this.allProducts = data.products.filter(p => p.handle && p.title);
        this.currency = data.currency || 'USD';
        this.truncated = data.truncated;
        this.securedProfiles = data.securedProfiles === true;
        this.profileRequests = new Map();
        // The owner requested this sourced data in the draft. Public launch remains a separate step.
        const role = window.Shopify?.theme?.role;
        this.externalRatings = ratingsForTheme(data, role, this.dataset.externalPreview !== 'false', this.dataset.publishedRelease === 'true');
      } catch {
        this.status.textContent = 'The racket catalog could not load. Please refresh or use the Racket Finder.';
        return;
      }
      this.estimates = false;
      this.products = this.allProducts.filter(p => this.securedProfiles || AXES.every(key => profile(p, false, this.externalRatings).values[key] !== null)).map(p => {
        const source = this.externalRatings[p.handle];
        const blank = {shape:null, bal:null, core:null, hand:null, wt:null, weightDisplay:null, surf:null, lvl:null, play:null, face:null, frame:null, rubber:null};
        const verified = Object.fromEntries(Object.entries(source?.display_specs || {}).filter(([key]) => Object.hasOwn(blank, key)));
        return source ? {...p, ...blank, ...verified} : p;
      });
      this.input = this.querySelector('[data-rc-search]');
      this.brand = this.querySelector('[data-rc-brand]');
      this.stock = this.querySelector('[data-rc-stock]');
      this.browse = this.querySelector('.rc__browse');
      this.workspace = this.querySelector('.rc__workspace');
      this.picker = this.querySelector('[data-rc-picker]');
      this.pickerBody = this.querySelector('[data-rc-picker-body]');
      this.chart = this.querySelector('[data-rc-chart]');
      this.tooltip = this.querySelector('[data-rc-tooltip]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.scores = new ScoreTooltip({ chart: this.chart, tooltip: this.tooltip, mobile: this.mobile,
        products: () => this.selected.map(h => this.products.find(p => p.handle === h)), ratings: () => this.externalRatings });
      this.limit = 6;
      this.selected = [];
      const params = new URL(location.href).searchParams;
      try {
        const saved = params.has('rackets') ? params.get('rackets').split(',') : JSON.parse(localStorage.getItem(STORAGE) || '[]');
        this.selected = selectHandles(saved, this.products);
        this.pendingSelections = Array.isArray(saved) ? saved.filter(h => this.allProducts.some(p => p.handle === h) && !this.products.some(p => p.handle === h)).length : 0;
      } catch { /* Storage is optional, including in private browsing. */ }
      const vendors = [...new Set(this.products.map(p => p.vendor).filter(Boolean))].sort();
      this.brand.innerHTML = '<option value="">All brands</option>' + vendors.map(v => `<option value="${esc(v)}">${esc(v)}</option>`).join('');
      on(this.input, 'input', () => { this.limit = 6; this.renderSearch(); });
      on(this.brand, 'change', () => { this.limit = 6; this.renderSearch(); });
      on(this.stock, 'change', () => { this.limit = 6; this.renderSearch(); });
      on(this, 'click', event => this.handleClick(event));
      on(this, 'click', event => this.trackProductClick(event));
      on(this.chart, 'pointermove', event => this.hoverScore(event));
      on(this.chart, 'pointerleave', () => {
        if (!this.scores.pinned && !this.chart.contains(document.activeElement)) this.hideScore();
      });
      on(this.chart, 'focusin', event => {
        const point = event.target.closest('[data-rc-point]');
        if (point) this.showScore(Number(point.dataset.rcPoint), Number(point.dataset.rcRacket));
      });
      on(this.chart, 'focusout', event => {
        if (!this.chart.contains(event.relatedTarget)) this.hideScore();
      });
      on(this.chart, 'keydown', event => this.scoreKey(event));
      on(document, 'pointerdown', event => {
        if (!this.chart.contains(event.target)) this.hideScore();
      });
      on(document, 'keydown', event => { if (event.key === 'Escape') this.hideScore(); });
      on(window, 'resize', () => this.hideScore());
      on(this.mobile, 'change', () => this.syncPickerLayout());
      on(this.picker, 'close', () => this.restorePickerFocus());
      on(this.picker, 'cancel', () => { this.querySelector('[data-rc-picker-status]').textContent = ''; });
      on(window, 'popstate', () => {
        const params = new URL(location.href).searchParams;
        this.selected = selectHandles((params.get('rackets') || '').split(','), this.products);
        this.thirdRequested = false;
        this.render();
        this.loadSelectedProfiles();
      });
      this.syncPickerLayout();
      this.render();
      this.loadSelectedProfiles();
      publish('pusa_comparator_viewed', { count: this.selected.length });
      if (this.pendingSelections) this.announce(`${this.pendingSelections} selected racket profile${this.pendingSelections === 1 ? ' is' : 's are'} awaiting verification.`);
    }

    disconnectedCallback() { this.restorePickerScroll(); this.abort?.abort(); this.ready = false; }

    fetchProfile(handle) {
      const product = this.products.find(p => p.handle === handle);
      if (product.verifiedValues) return Promise.resolve(product);
      if (this.profileRequests.has(handle)) return this.profileRequests.get(handle);
      const request = (async () => {
        const url = new URL(product.url, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid product URL');
        url.searchParams.set('section_id', 'racket-play-profile-one_custom');
        const theme = window.Shopify?.theme;
        if (theme?.id && theme.role !== 'main') url.searchParams.set('preview_theme_id', String(theme.id));
        const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: this.abort.signal });
        if (!response.ok) throw new Error('Profile unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const reply = JSON.parse(doc.querySelector('[data-rpp-one]')?.textContent || 'null');
        // 2026-10-31: scores are not in the theme; they load from ratings.padelusa.com by product id.
        if (!window.PadelRatings) throw new Error('Ratings unavailable');
        if (reply) reply.verifiedValues = (await window.PadelRatings.scores([reply.id])).values[reply.id];
        const verified = verifiedProduct(product, reply);
        Object.assign(product, verified);
        return product;
      })().catch(error => { this.profileRequests.delete(handle); throw error; });
      this.profileRequests.set(handle, request);
      return request;
    }

    async loadSelectedProfiles() {
      if (!this.securedProfiles || !this.selected.length) return;
      const handles = this.selected.slice();
      this.announce('Loading racket profiles…');
      const results = await Promise.allSettled(handles.map(handle => this.fetchProfile(handle)));
      if (this.abort.signal.aborted || handles.join(',') !== this.selected.join(',')) return;
      this.render();
      this.announce(results.some(result => result.status === 'rejected') ?
        'A racket profile could not load. Remove and add that racket again to retry.' : 'Racket profiles loaded.');
    }

    syncPickerLayout() {
      if (this.mobile.matches) this.pickerBody.append(this.browse);
      else {
        if (this.picker.open) this.picker.close();
        this.workspace.prepend(this.browse);
      }
    }

    openPicker() {
      if (!this.mobile.matches) {
        this.input.focus();
        this.input.scrollIntoView({ behavior: 'auto', block: 'center' });
        return;
      }
      this.syncPickerLayout();
      if (!this.picker.open) {
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.picker.showModal();
      }
      this.input.focus({ preventScroll: true });
    }

    restorePickerScroll() {
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }

    restorePickerFocus() {
      this.restorePickerScroll();
      const add = this.querySelector('[data-rc-mobile-add]');
      const target = add?.hidden ? this.querySelector('[data-rc-third]') : add?.disabled ? this.querySelector('[data-rc-remove]') : add;
      target?.focus({ preventScroll: true });
    }

    focusAfterChange() {
      if (this.picker.open) this.picker.close();
      else if (this.mobile.matches) this.restorePickerFocus();
      else this.input.focus({ preventScroll: true });
    }

    money(value) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.currency }).format(value);
    }

    announce(message) {
      this.status.textContent = message;
      this.querySelector('[data-rc-picker-status]').textContent = this.picker?.open ? message : '';
    }

    // Exact-score panel behavior lives in ScoreTooltip (shared with the PDP "How it plays" module).
    hideScore() { this.scores.hide(); }
    hoverScore(event) { this.scores.hover(event); }
    pointAtEvent(event) { return this.scores.pointAt(event); }
    pointerAxis(event) { return this.scores.axisAt(event); }
    showScore(axis, index, pinned = false) { this.scores.show(axis, index, pinned); }
    scoreKey(event) { this.scores.key(event); }

    // View, choose-options and add-to-cart clicks on a compared racket (analytics only).
    trackProductClick(event) {
      const form = event.target.closest('product-form-component');
      const quick = event.target.closest('quick-add-component');
      const link = event.target.closest('a[href]');
      if (form && event.target.closest('button[type="submit"], button[name="add"]')) {
        publish('pusa_comparator_product_clicked', { action: 'add', product_id: form.dataset.productId || null, count: this.selected.length });
      } else if (quick && event.target.closest('button')) {
        publish('pusa_comparator_product_clicked', { action: 'choose', handle: handleOf(quick.dataset.productUrl), count: this.selected.length });
      } else if (link && this.contains(link) && handleOf(link.href)) {
        publish('pusa_comparator_product_clicked', { action: 'view', handle: handleOf(link.href), count: this.selected.length });
      }
    }

    async handleClick(event) {
      const point = this.pointAtEvent(event);
      const polygon = event.target.closest('[data-rc-polygon]');
      if (point) {
        if (event.detail > 0) point.focus({ preventScroll: true });
        this.showScore(Number(point.dataset.rcPoint), Number(point.dataset.rcRacket), this.mobile.matches);
        return;
      }
      if (polygon) { this.showScore(this.pointerAxis(event), Number(polygon.dataset.rcPolygon), this.mobile.matches); return; }
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-rc-picker-close')) { this.picker.close(); return; }
      if (button.hasAttribute('data-rc-score-close')) { this.hideScore(); return; }
      if (button.hasAttribute('data-rc-third')) {
        this.thirdRequested = true;
        this.render();
        this.openPicker();
        return;
      }
      if (button.hasAttribute('data-rc-add')) {
        const handle = button.dataset.rcAdd;
        if (this.selected.length >= comparisonCapacity(this.selected, this.thirdRequested)) { this.announce('Add a third slot or remove a racket to try another.'); return; }
        if (!this.products.some(p => p.handle === handle) || this.selected.includes(handle)) return;
        this.selected.push(handle);
        this.changed();
        publish('pusa_comparator_racket_added', { handle, count: this.selected.length });
        if (this.selected.length >= 2) publish('pusa_comparator_compared', { handles: this.selected.slice(0, MAX), count: this.selected.length });
        this.announce(`${this.products.find(p => p.handle === handle).title} added. ${this.selected.length} rackets selected.`);
        this.focusAfterChange();
      } else if (button.hasAttribute('data-rc-remove')) {
        this.selected = this.selected.filter(h => h !== button.dataset.rcRemove);
        this.thirdRequested = false;
        this.changed();
        this.announce('Racket removed.');
        this.focusAfterChange();
      } else if (button.hasAttribute('data-rc-clear')) {
        this.selected = [];
        this.thirdRequested = false;
        this.changed();
        this.announce('Comparison cleared.');
        this.focusAfterChange();
      } else if (button.hasAttribute('data-rc-more')) {
        this.limit += 6;
        this.renderSearch();
        this.querySelectorAll('[data-rc-add]')[this.limit - 6]?.focus({ preventScroll: true });
      } else if (button.hasAttribute('data-rc-slot')) {
        this.openPicker();
      } else if (button.hasAttribute('data-rc-share')) {
        const url = this.comparisonUrl().href;
        try {
          await navigator.clipboard.writeText(url);
          this.announce('Comparison link copied.');
        } catch {
          const field = this.querySelector('[data-rc-share-link]');
          field.parentElement.hidden = false;
          field.value = url;
          field.focus(); field.select();
          this.announce('Copy the selected comparison link.');
        }
      }
    }

    comparisonUrl() {
      const url = new URL(location.href);
      url.searchParams.set('rackets', this.selected.join(','));
      // Never put a preview theme into a link people share (engine protection P0-5): unreleased
      // work would reach anyone who opens it. The previewer's own cookie keeps their preview.
      url.searchParams.delete('preview_theme_id');
      return url;
    }

    changed() {
      try { localStorage.setItem(STORAGE, JSON.stringify(this.selected)); } catch { /* Optional. */ }
      try { history.replaceState(history.state, '', this.comparisonUrl()); } catch { /* Embedded preview. */ }
      this.querySelector('[data-rc-share-link]').parentElement.hidden = true;
      this.render();
      this.loadSelectedProfiles();
    }

    renderSearch() {
      const results = search(this.products, this.input.value, this.brand.value, this.stock.checked, this.selected);
      this.querySelector('[data-rc-count]').textContent = `${results.length} racket${results.length === 1 ? '' : 's'}${this.truncated ? ' in this catalog page' : ''}`;
      const capacity = comparisonCapacity(this.selected, this.thirdRequested);
      const full = this.selected.length >= capacity;
      this.querySelector('[data-rc-results]').innerHTML = results.length ? results.slice(0, this.limit).map(p => `
        <button type="button" class="rc__result" data-rc-add="${esc(p.handle)}" ${full ? 'disabled' : ''} aria-label="Add ${esc(p.title)} to comparison">
          ${p.img ? `<img src="${esc(p.img)}" alt="" width="64" height="80" loading="lazy">` : '<span class="rc__no-image" aria-hidden="true">—</span>'}
          <span class="rc__result-text"><span class="rc__vendor">${esc(p.vendor)}</span><span class="rc__result-title">${esc(p.title)}</span><span class="rc__meta">${p.priceVaries ? 'From ' : ''}${esc(this.money(p.price))}${p.available ? '' : ' · Sold out'}</span></span>
          <span class="rc__plus" aria-hidden="true">+</span>
        </button>`).join('') : '<p class="rc__no-results">No rackets match. Try a different model or brand, or include sold-out rackets.</p>';
      const more = this.querySelector('[data-rc-more]');
      more.hidden = results.length <= this.limit;
      more.disabled = full;
      this.querySelector('[data-rc-limit-note]').hidden = !full;
      this.querySelector('[data-rc-limit-note]').textContent = capacity === 2 ? 'Two rackets selected. Add a third racket above, or remove one to try another.' : 'Three rackets selected. Remove one to try another.';
    }

    render() {
      const selected = this.selected.map(h => this.products.find(p => p.handle === h));
      const capacity = comparisonCapacity(this.selected, this.thirdRequested);
      this.hideScore();
      this.querySelector('[data-rc-selection-count]').textContent = `${selected.length} / ${capacity} selected`;
      this.querySelector('[data-rc-clear]').disabled = !selected.length;
      this.querySelector('[data-rc-share]').disabled = !selected.length;
      this.querySelector('[data-rc-mobile-add]').disabled = selected.length >= MAX;
      this.querySelector('[data-rc-mobile-add]').hidden = selected.length === 2 && capacity === 2;
      this.querySelector('[data-rc-third]').hidden = selected.length !== 2 || capacity !== 2;
      this.querySelector('[data-rc-cards]').style.setProperty('--rc-columns', capacity);
      this.querySelector('[data-rc-cards]').innerHTML = Array.from({ length: capacity }, (_, index) => {
        const p = selected[index];
        if (!p) return `<button type="button" class="rc__slot" data-rc-slot><span class="rc__slot-plus" aria-hidden="true">+</span><span>Add ${index === 0 ? 'your first' : 'a'} racket</span></button>`;
        return `<article class="rc__card rc__series--${index}">
          <div class="rc__card-top"><span class="rc__color-key" aria-hidden="true"></span><span class="rc__vendor">Racket ${index + 1} · ${esc(p.vendor)}</span><button type="button" class="rc__remove" data-rc-remove="${esc(p.handle)}" aria-label="Remove ${esc(p.title)}">×</button></div>
          <a class="rc__card-image" href="${esc(p.url)}">${p.img ? `<img src="${esc(p.img)}" alt="${esc(p.title)}" width="160" height="180">` : '<span>Image unavailable</span>'}</a>
          <h3 class="rc__card-title"><a href="${esc(p.url)}">${esc(p.title)}</a></h3>
          <p class="rc__price">${!p.priceVaries && p.cap > p.price ? `<s>${esc(this.money(p.cap))}</s> ` : ''}${p.priceVaries ? 'From ' : ''}${esc(this.money(p.price))}</p>
          <p class="rc__meta">${p.available ? 'In stock' : 'Sold out'}${p.shape?.length ? ' · ' + esc(display(p.shape)) : ''}</p>
          <div class="rc__card-actions"><a class="button button-secondary rc__view" href="${esc(p.url)}">View racket</a>${purchaseMarkup(p)}</div>
        </article>`;
      }).join('');
      this.chart.querySelector('[data-rc-plot]').innerHTML = svgChart(selected, this.estimates, this.externalRatings);
      this.querySelector('[data-rc-chart-title]').textContent = selected.length > 1 ? 'See the trade-offs' : 'Racket performance';
      const hasCompleteProfile = selected.some(p => AXES.every(key => profile(p, this.estimates, this.externalRatings).values[key] !== null));
      const chartEmpty = this.querySelector('[data-rc-chart-empty]');
      chartEmpty.hidden = hasCompleteProfile;
      chartEmpty.innerHTML = selected.length ? 'Loading racket profiles…<br>Exact ratings will appear when the profile is ready.' : 'Add a racket to see its profile.<br>Add a second to see how they differ.';
      this.querySelector('[data-rc-legend]').innerHTML = selected.map((p, i) =>
        `<li class="rc__series--${i}"><span class="rc__color-key" aria-hidden="true"></span><span class="rc__legend-name">${i + 1}. ${esc(p.title)}</span></li>`
      ).join('');
      const tableArea = this.querySelector('[data-rc-table-area]');
      tableArea.hidden = !selected.length;
      if (selected.length) this.renderTable(selected);
      this.renderSearch();
    }

    renderTable(products) {
      const toggle = this.querySelector('[data-rc-differences]');
      toggle.disabled = products.length < 2;
      if (toggle.disabled) toggle.checked = false;
      this.querySelector('[data-rc-differences-label]').hidden = products.length < 2;
      this.querySelector('[data-rc-details-title]').textContent = products.length > 1 ? 'Side by side' : 'Performance details';
      const differencesOnly = toggle.checked;
      const comparisons = ratingComparisons(products, this.externalRatings);
      const summary = this.querySelector('[data-rc-summary]');
      summary.hidden = !products.length;
      summary.setAttribute('aria-label', products.length > 1 ? 'Main rating trade-offs' : 'Performance overview');
      summary.innerHTML = products.length > 1 ? tradeoffSummary(products, comparisons) : performanceSummary(products[0], this.externalRatings);
      this.querySelector('[data-rc-difference-note]').hidden = !differencesOnly;
      this.querySelector('[data-rc-table]').classList.toggle('rc__table--differences', differencesOnly);
      const specs = [
        ['Shape', p => display(p.shape)], ['Balance', p => display(p.bal)],
        ['Weight', p => p.weightDisplay || display(p.wt)], ['Core feel', p => display(p.core)],
        ['Surface', p => display(p.surf)], ['Handling', p => display(p.hand)],
        ['Player level', p => display(p.lvl)], ['Style of play', p => display(p.play).replace(/Aggresive/g, 'Aggressive')],
        ['Face material', p => p.face || 'Not listed'], ['Frame material', p => display(p.frame)],
        ['Core material', p => display(p.rubber)]
      ];
      const row = (label, values) => {
        if (differencesOnly && products.length > 1 && values.every(v => v === values[0])) return '';
        return `<tr><th scope="row">${esc(label)}</th>${values.map(v => `<td class="${differencesOnly ? 'rc__spec-difference' : ''}">${esc(v)}</td>`).join('')}</tr>`;
      };
      let html = `<caption class="visually-hidden">Racket attributes and specifications. Missing values are not listed.</caption><thead><tr><th scope="col">Attribute</th>${products.map((p, i) => `<th scope="col" class="rc__series--${i}"><span class="rc__color-key" aria-hidden="true"></span> <span class="rc__table-name">${i + 1}. ${esc(p.title)}</span></th>`).join('')}</tr></thead><tbody>`;
      const ratings = (differencesOnly ? [...comparisons].sort((a, b) => (b.spread ?? -1) - (a.spread ?? -1) || a.axis - b.axis) : comparisons)
        .filter(row => !differencesOnly || !row.values.every(value => value === row.values[0]))
        .map(row => `<tr><th scope="row">${row.label}</th>${ratingCells(row, differencesOnly, products)}</tr>`).join('');
      const specifications = specs.map(([label, value]) => { const values = products.map(value); return values.every(v => v === 'Not listed') ? '' : row(label, values); }).join('');
      html += ratings + specifications || `<tr><td colspan="${products.length + 1}" class="rc__table-empty">None of the listed values differ.</td></tr>`;
      this.querySelector('[data-rc-table]').innerHTML = html + '</tbody>';
    }
  }
  customElements.define('racket-comparator', RacketComparator);
  // Checkbox lives outside renderTable so focus survives table updates.
  document.addEventListener('change', event => {
    if (event.target.matches('[data-rc-differences]')) {
      const app = event.target.closest('racket-comparator');
      if (app?.ready) app.renderTable(app.selected.map(h => app.products.find(p => p.handle === h)));
    }
  });
})();
